import axios from 'axios';

const log = require("loglevel").getLogger("LicenseService");
log.setLevel("error");

/**
 * License API Service
 * 
 * Domain Logic:
 * - Fetches license information from backend API
 * - Handles authentication headers for license requests
 * - Provides error handling for license data retrieval
 * - Returns structured license data for frontend consumption
 */

/**
 * Fetches license information for the current user/organization
 * 
 * @returns {Promise<Object>} License information including client_id, status, dates, etc.
 */
export const getLicenseInfo = () => {
	return axios.post(`${process.env.REACT_APP_HOST_URL}/license/get`, {})
		.then(async response => {
			const res = response.data;
			log.debug("LicenseService -> getLicenseInfo -> return");
			return res;
		})
		.catch(response => {
			log.debug("LicenseService -> getLicenseInfo -> error: ");
			log.debug(response);
			return null;
		});
};

/**
 * Fetches all licenses with pagination support
 * 
 * @param {number} page - Page number (default: 1)
 * @param {number} limit - Number of items per page (default: 10)
 * @returns {Promise<Object>} License list with metadata
 */
export const getLicenses = (page = 1, limit = 10) => {
	return axios.get(`${process.env.REACT_APP_HOST_URL}/license?page=${page}&limit=${limit}`)
		.then(async response => {
			const res = response.data;
			log.debug("LicenseService -> getLicenses -> return");
			return res;
		})
		.catch(response => {
			log.debug("LicenseService -> getLicenses -> error: ");
			log.debug(response);
			return null;
		});
};

/**
 * Creates a new license with the specified pricing tier
 * 
 * @param {number} pricingTierId - ID of the pricing tier
 * @returns {Promise<Object>} Created license information
 */
export const createLicense = (pricingTierId) => {
	return axios.post(`${process.env.REACT_APP_HOST_URL}/license/create`, {
		pricing_tier_id: pricingTierId
	})
		.then(async response => {
			const res = response.data;
			log.debug("LicenseService -> createLicense -> return");
			return res;
		})
		.catch(response => {
			log.debug("LicenseService -> createLicense -> error: ");
			log.debug(response);
			return null;
		});
};

/**
 * Fetches all available pricing tiers
 * 
 * @returns {Promise<Object>} List of pricing tiers
 */
export const getPricingTiers = () => {
	return axios.get(`${process.env.REACT_APP_HOST_URL}/license/pricing-tier`)
		.then(async response => {
			const res = response.data;
			log.debug("LicenseService -> getPricingTiers -> return");
			return res;
		})
		.catch(response => {
			log.debug("LicenseService -> getPricingTiers -> error: ");
			log.debug(response);
			return null;
		});
};

/**
 * Deletes a license by ID
 * 
 * @param {number} licenseId - ID of the license to delete
 * @returns {Promise<Object>} Deletion result
 */
export const deleteLicense = (licenseId) => {
	return axios.delete(`${process.env.REACT_APP_HOST_URL}/license/${licenseId}`)
		.then(async response => {
			const res = response.data;
			log.debug("LicenseService -> deleteLicense -> return");
			return res;
		})
		.catch(response => {
			log.debug("LicenseService -> deleteLicense -> error: ");
			log.debug(response);
			return null;
		});
}; 